import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import toast from "react-hot-toast";
import clsx from "clsx";
import {
  HiOutlineClock,
  HiOutlineCalendar,
  HiOutlineLocationMarker,
  HiOutlineVideoCamera,
  HiOutlinePhone,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlinePencil,
  HiOutlineRefresh,
  HiOutlineBan,
  HiOutlineExternalLink,
  HiOutlineDocumentText,
  HiOutlineChatAlt2,
  HiOutlineBell,
  HiOutlineInformationCircle,
} from "react-icons/hi";
import { Modal, Button, Badge, Spinner, Textarea, Avatar } from "../../components/UI";
import { visitsAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  VISIT_TYPE_MAP,
  VISIT_STATUSES,
  fullName,
  formatMinutes,
  plannedMinutes,
  mapsUrl,
} from "./visitConstants";
import useVisitOutcomes from "./useVisitOutcomes";

const fmt = (d, pattern = "dd MMM yyyy, hh:mm a") => (d ? format(new Date(d), pattern) : "—");

// Terminal states that end the visit without check-out
const STOPPED = {
  cancelled: "Cancelled",
  rejected: "Rejected",
  no_show: "No show",
  rescheduled: "Rescheduled",
};

const SectionTitle = ({ icon: Icon, children }) => (
  <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-500 mb-2">
    <Icon className="w-3.5 h-3.5" />
    {children}
  </p>
);

const InfoTile = ({ icon: Icon, label, children, action }) => (
  <div className="flex items-start gap-3 rounded-xl border border-secondary-100 bg-white p-3">
    <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-[18px] h-[18px] text-secondary-500" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-400">{label}</p>
      <div className="text-sm font-medium text-secondary-900 break-words mt-0.5">{children}</div>
    </div>
    {action}
  </div>
);

const LinkButton = ({ href, children, icon: Icon = HiOutlineExternalLink }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="self-center inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors flex-shrink-0"
  >
    <Icon className="w-3.5 h-3.5" />
    {children}
  </a>
);

// Scheduled → Checked in → Checked out, or Scheduled → <stopped state>
const ProgressTrack = ({ visit }) => {
  const stopped = STOPPED[visit.status];
  const steps = [
    {
      label: visit.status === "pending_approval" ? "Awaiting approval" : "Scheduled",
      time: fmt(visit.scheduledStart, "dd MMM, hh:mm a"),
      done: visit.status !== "pending_approval",
      current: ["pending_approval", "scheduled"].includes(visit.status),
    },
    stopped
      ? { label: stopped, time: fmt(visit.updatedAt, "dd MMM, hh:mm a"), done: true, stopped: true }
      : {
          label: "Checked in",
          time: visit.startedAt ? fmt(visit.startedAt, "hh:mm a") : "Not started",
          done: !!visit.startedAt,
          current: visit.status === "in_progress",
        },
    ...(stopped
      ? []
      : [
          {
            label: "Checked out",
            time: visit.endedAt ? fmt(visit.endedAt, "hh:mm a") : "—",
            done: !!visit.endedAt,
          },
        ]),
  ];

  return (
    <ol className="flex items-start">
      {steps.map((s, i) => (
        <li key={s.label} className="flex-1 flex flex-col items-center text-center relative">
          {i > 0 && (
            <span
              className={clsx(
                "absolute top-3.5 right-1/2 w-full h-0.5 -z-0",
                s.done ? (s.stopped ? "bg-danger-200" : "bg-success-300") : "bg-secondary-200"
              )}
              aria-hidden="true"
            />
          )}
          <span
            className={clsx(
              "relative z-[1] w-7 h-7 rounded-full flex items-center justify-center border-2",
              s.stopped
                ? "bg-danger-500 border-danger-500 text-white"
                : s.done
                ? "bg-success-500 border-success-500 text-white"
                : s.current
                ? "bg-white border-primary-500 text-primary-600"
                : "bg-white border-secondary-300 text-secondary-300"
            )}
          >
            {s.stopped ? (
              <HiOutlineX className="w-4 h-4" />
            ) : s.done ? (
              <HiOutlineCheck className="w-4 h-4" />
            ) : (
              <span className={clsx("w-2 h-2 rounded-full", s.current ? "bg-primary-500 animate-pulse" : "bg-secondary-300")} />
            )}
          </span>
          <span className={clsx("mt-1.5 text-xs font-semibold", s.done || s.current ? "text-secondary-800" : "text-secondary-400")}>
            {s.label}
          </span>
          <span className="text-[11px] text-secondary-500">{s.time}</span>
        </li>
      ))}
    </ol>
  );
};

/**
 * Visit detail with approve / reject / edit / reschedule / cancel actions.
 * onEdit(visit) and onReschedule(visit) open VisitModal in the parent.
 */
const VisitDetailModal = ({ visitId, onClose, onEdit, onReschedule, onScheduleNext, onOpenVisit, onChanged }) => {
  const { isAdmin, user } = useAuth();
  const queryClient = useQueryClient();
  const { outcomeInfo } = useVisitOutcomes();
  const [reasonFor, setReasonFor] = useState(null); // 'reject' | 'cancel'
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: visit, isLoading } = useQuery({
    queryKey: ["visit", visitId],
    queryFn: async () => (await visitsAPI.getById(visitId)).data?.data,
    enabled: !!visitId,
  });

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(res.data?.message || okMsg);
      queryClient.invalidateQueries({ queryKey: ["visit", visitId] });
      onChanged?.();
      setReasonFor(null);
      setReason("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setReasonFor(null);
    setReason("");
    onClose();
  };

  const type = VISIT_TYPE_MAP[visit?.type] || VISIT_TYPE_MAP.site_visit;
  const status = VISIT_STATUSES[visit?.status] || VISIT_STATUSES.scheduled;
  const TypeIcon = type.icon;
  const upcoming = ["pending_approval", "scheduled"].includes(visit?.status);
  const isOwner = String(visit?.assignedTo?._id || "") === String(user?._id || "");
  const canManage = isAdmin || isOwner;
  const canScheduleNext = canManage && visit?.status === "completed" && !visit?.nextVisit && !!onScheduleNext;
  const showActions = canManage && (upcoming || visit?.status === "no_show" || canScheduleNext);
  const lateMinutes =
    visit?.startedAt && visit?.scheduledStart
      ? Math.round((new Date(visit.startedAt) - new Date(visit.scheduledStart)) / 60000)
      : null;
  const outcome = outcomeInfo(visit);
  const scheduledByOther =
    visit?.createdBy && String(visit.createdBy._id) !== String(visit.assignedTo?._id);

  const stats = visit
    ? [
        { label: "Planned", value: formatMinutes(plannedMinutes(visit)) },
        {
          label: "Actual",
          value: visit.status === "completed" ? formatMinutes(visit.actualMinutes) : "—",
          tone: visit.status === "completed" ? "text-secondary-900" : "text-secondary-400",
        },
        {
          label: "Punctuality",
          value: lateMinutes === null ? "—" : lateMinutes <= 0 ? "On time" : `${formatMinutes(lateMinutes)} late`,
          tone: lateMinutes === null ? "text-secondary-400" : lateMinutes <= 0 ? "text-success-600" : "text-warning-600",
        },
      ]
    : [];

  return (
    <Modal isOpen={!!visitId} onClose={close} title="Visit Details" size="lg">
      {isLoading || !visit ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Hero */}
          <div
            className="relative overflow-hidden rounded-2xl border p-4"
            style={{ borderColor: `${type.color}33`, background: `linear-gradient(135deg, ${type.color}14, ${type.color}05)` }}
          >
            <div className="flex items-start gap-3.5">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                style={{ backgroundColor: type.color }}
              >
                <TypeIcon className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: type.color }}>
                      {type.label}
                    </p>
                    <h3 className="text-lg font-bold text-secondary-900 leading-tight truncate">
                      {visit.leadId?._id ? (
                        <Link to={`/app/leads/${visit.leadId._id}`} onClick={close} className="hover:text-primary-600 hover:underline">
                          {visit.leadId.name}
                        </Link>
                      ) : (
                        "Deleted lead"
                      )}
                    </h3>
                    {visit.title && <p className="text-sm text-secondary-600 truncate">{visit.title}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge variant={status.variant} size="md">
                      {status.label}
                    </Badge>
                    {visit.autoClosed && <Badge variant="warning">Auto-closed</Badge>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-secondary-600">
                  <span className="inline-flex items-center gap-1">
                    <HiOutlineCalendar className="w-3.5 h-3.5" />
                    {fmt(visit.scheduledStart, "EEE, dd MMM yyyy")}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <HiOutlineClock className="w-3.5 h-3.5" />
                    {fmt(visit.scheduledStart, "hh:mm a")} – {fmt(visit.scheduledEnd, "hh:mm a")}
                  </span>
                  {visit.leadId?.phone && (
                    <a href={`tel:${visit.leadId.phone}`} className="inline-flex items-center gap-1 hover:text-primary-600">
                      <HiOutlinePhone className="w-3.5 h-3.5" />
                      {visit.leadId.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="rounded-2xl border border-secondary-100 bg-white px-3 py-4">
            <ProgressTrack visit={visit} />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl bg-secondary-50 border border-secondary-100 px-3 py-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">{s.label}</p>
                <p className={clsx("text-base font-bold mt-0.5", s.tone || "text-secondary-900")}>{s.value}</p>
              </div>
            ))}
            <div className="rounded-xl bg-secondary-50 border border-secondary-100 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">Outcome</p>
              {outcome ? (
                <span
                  className="inline-block mt-1 px-2 py-0.5 rounded-md border text-xs font-bold"
                  style={{ color: outcome.color, borderColor: `${outcome.color}55`, backgroundColor: `${outcome.color}14` }}
                >
                  {outcome.label}
                </span>
              ) : (
                <p className="text-base font-bold mt-0.5 text-secondary-400">—</p>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <InfoTile icon={HiOutlineInformationCircle} label="Employee">
              <span className="inline-flex items-center gap-2">
                <Avatar name={fullName(visit.assignedTo)} size="sm" className="ring-0 shadow-none" />
                <span>
                  {fullName(visit.assignedTo) || "—"}
                  {scheduledByOther && (
                    <span className="block text-xs font-normal text-secondary-500">
                      Scheduled by {fullName(visit.createdBy)}
                    </span>
                  )}
                </span>
              </span>
            </InfoTile>

            {visit.type === "online_meeting" ? (
              <InfoTile
                icon={HiOutlineVideoCamera}
                label="Google Meet"
                action={visit.meetLink ? <LinkButton href={visit.meetLink}>Join</LinkButton> : null}
              >
                {visit.meetLink ? (
                  <span className="text-secondary-700 break-all">{visit.meetLink.replace(/^https?:\/\//, "")}</span>
                ) : (
                  <span className="text-secondary-400 font-normal">No link added</span>
                )}
              </InfoTile>
            ) : (
              <InfoTile
                icon={HiOutlineLocationMarker}
                label="Customer address"
                action={visit.address ? <LinkButton href={mapsUrl(visit.location, visit.address)}>Map</LinkButton> : null}
              >
                {visit.address || <span className="text-secondary-400 font-normal">No address</span>}
              </InfoTile>
            )}

            {(visit.startedAt || visit.endedAt) && (
              <>
                <InfoTile
                  icon={HiOutlineLocationMarker}
                  label={`Check-in · ${fmt(visit.startedAt, "hh:mm a")}`}
                  action={mapsUrl(visit.startLocation) ? <LinkButton href={mapsUrl(visit.startLocation)}>Map</LinkButton> : null}
                >
                  {visit.startLocation ? (
                    <span className="text-secondary-700">
                      Location captured
                      {Number.isFinite(visit.startLocation.accuracy) && (
                        <span className="text-secondary-400 font-normal"> · ±{Math.round(visit.startLocation.accuracy)}m</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-secondary-400 font-normal">No location</span>
                  )}
                </InfoTile>
                <InfoTile
                  icon={HiOutlineLocationMarker}
                  label={`Check-out${visit.endedAt ? ` · ${fmt(visit.endedAt, "hh:mm a")}` : ""}`}
                  action={mapsUrl(visit.endLocation) ? <LinkButton href={mapsUrl(visit.endLocation)}>Map</LinkButton> : null}
                >
                  {visit.endedAt ? (
                    visit.endLocation ? (
                      <span className="text-secondary-700">
                        Location captured
                        {Number.isFinite(visit.endLocation.accuracy) && (
                          <span className="text-secondary-400 font-normal"> · ±{Math.round(visit.endLocation.accuracy)}m</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-secondary-400 font-normal">No location</span>
                    )
                  ) : (
                    <span className="text-primary-600">In progress…</span>
                  )}
                </InfoTile>
              </>
            )}
          </div>

          {/* Agenda / notes */}
          {visit.agenda && (
            <div>
              <SectionTitle icon={HiOutlineDocumentText}>Agenda</SectionTitle>
              <p className="text-sm text-secondary-800 whitespace-pre-wrap rounded-xl bg-secondary-50 border border-secondary-100 px-3.5 py-3">
                {visit.agenda}
              </p>
            </div>
          )}
          {visit.outcomeNotes && (
            <div>
              <SectionTitle icon={HiOutlineChatAlt2}>Visit notes</SectionTitle>
              <p className="text-sm text-secondary-800 whitespace-pre-wrap rounded-xl bg-secondary-50 border border-secondary-100 px-3.5 py-3">
                {visit.outcomeNotes}
              </p>
            </div>
          )}

          {visit.nextFollowUpAt && (
            <div className="flex items-center gap-3 rounded-xl border border-primary-200 bg-primary-50 px-3.5 py-3">
              <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center flex-shrink-0">
                <HiOutlineBell className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-primary-800">Follow-up scheduled</p>
                <p className="text-xs text-primary-700">
                  {fmt(visit.nextFollowUpAt, "EEE, dd MMM yyyy · hh:mm a")} — listed in Follow-ups
                </p>
              </div>
            </div>
          )}

          {/* Status notes */}
          {visit.status === "rejected" && (
            <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-3 text-sm text-danger-700">
              <span className="font-semibold">Rejected by {fullName(visit.approval?.rejectedBy) || "admin"}</span>
              {visit.approval?.rejectedReason ? ` — ${visit.approval.rejectedReason}` : ""}
            </div>
          )}
          {["cancelled", "rescheduled"].includes(visit.status) && visit.cancelReason && (
            <div className="rounded-xl border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-sm text-secondary-700">
              <span className="font-semibold">Reason:</span> {visit.cancelReason}
            </div>
          )}
          {(visit.rescheduledFrom || visit.rescheduledTo || (visit.approval?.required && visit.approval?.approvedBy)) && (
            <div className="space-y-1 text-xs text-secondary-500">
              {visit.approval?.required && visit.approval?.approvedBy && (
                <p>
                  Approved by {fullName(visit.approval.approvedBy)} on {fmt(visit.approval.approvedAt)}
                </p>
              )}
              {visit.rescheduledFrom && <p>Rescheduled from {fmt(visit.rescheduledFrom.scheduledStart)}</p>}
              {visit.rescheduledTo && <p>Moved to {fmt(visit.rescheduledTo.scheduledStart)}</p>}
            </div>
          )}

          {/* Repeat visits */}
          {(visit.previousVisitId || visit.nextVisit) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {visit.previousVisitId && (
                <button
                  type="button"
                  onClick={() => onOpenVisit?.(visit.previousVisitId._id)}
                  className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-white px-3.5 py-3 text-left hover:border-primary-300 transition-colors"
                >
                  <HiOutlineRefresh className="w-5 h-5 text-secondary-400 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-secondary-400">Repeat visit of</span>
                    <span className="block text-sm font-semibold text-secondary-800 truncate">
                      {fmt(visit.previousVisitId.scheduledStart, "dd MMM yyyy")}
                      {visit.previousVisitId.outcomeLabel ? ` · ${visit.previousVisitId.outcomeLabel}` : ""}
                    </span>
                  </span>
                </button>
              )}
              {visit.nextVisit && (
                <button
                  type="button"
                  onClick={() => onOpenVisit?.(visit.nextVisit._id)}
                  className="flex items-center gap-3 rounded-xl border border-primary-200 bg-primary-50 px-3.5 py-3 text-left hover:border-primary-400 transition-colors"
                >
                  <HiOutlineCalendar className="w-5 h-5 text-primary-600 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-primary-500">Next visit</span>
                    <span className="block text-sm font-semibold text-primary-800 truncate">
                      {fmt(visit.nextVisit.scheduledStart, "dd MMM yyyy, hh:mm a")} · {VISIT_STATUSES[visit.nextVisit.status]?.label}
                    </span>
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Action bar (pinned to the modal bottom) */}
          {showActions && (
            <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-3.5 bg-white border-t border-secondary-100">
              {reasonFor ? (
                <div className="space-y-2.5">
                  <Textarea
                    label={reasonFor === "reject" ? "Reason for rejection" : "Reason for cancelling"}
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Optional — shared with the employee"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setReasonFor(null)} disabled={busy}>
                      Back
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      loading={busy}
                      onClick={() =>
                        reasonFor === "reject"
                          ? run(() => visitsAPI.reject(visit._id, reason), "Visit rejected")
                          : run(() => visitsAPI.cancel(visit._id, reason), "Visit cancelled")
                      }
                    >
                      {reasonFor === "reject" ? "Reject Visit" : "Cancel Visit"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {upcoming && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                      icon={<HiOutlineBan className="w-4 h-4" />}
                      onClick={() => setReasonFor("cancel")}
                    >
                      Cancel Visit
                    </Button>
                  )}
                  <div className="flex flex-wrap gap-2 ml-auto">
                    {canScheduleNext && (
                      <Button size="sm" icon={<HiOutlineCalendar className="w-4 h-4" />} onClick={() => onScheduleNext(visit)}>
                        Schedule Next Visit
                      </Button>
                    )}
                    {(upcoming || visit.status === "no_show") && (
                      <Button size="sm" variant="outline" icon={<HiOutlineRefresh className="w-4 h-4" />} onClick={() => onReschedule(visit)}>
                        Reschedule
                      </Button>
                    )}
                    {upcoming && (
                      <Button size="sm" variant="outline" icon={<HiOutlinePencil className="w-4 h-4" />} onClick={() => onEdit(visit)}>
                        Edit
                      </Button>
                    )}
                    {isAdmin && visit.status === "pending_approval" && (
                      <>
                        <Button size="sm" variant="danger" icon={<HiOutlineX className="w-4 h-4" />} onClick={() => setReasonFor("reject")}>
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="success"
                          loading={busy}
                          icon={<HiOutlineCheck className="w-4 h-4" />}
                          onClick={() => run(() => visitsAPI.approve(visit._id), "Visit approved")}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default VisitDetailModal;
